import type { ReactNode } from "react";
import { BrowserRouter } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { AuthProvider } from "@/hooks/use-auth";
import { ThemeProvider } from "@/hooks/use-theme";

interface AppProvidersProps {
  children: ReactNode;
  /** Overridden in tests so runs don't inherit each other's persisted theme. */
  defaultTheme?: "dark" | "light" | "system";
  storageKey?: string;
}

/**
 * The single definition of the app's provider stack.
 *
 * Both `main.tsx` and the test harness (`src/test/utils.tsx`) render through
 * this, so a provider that tests supply can never be one production forgets.
 * They used to be maintained separately, and drifted: `Toaster` is a context
 * provider that renders its `children`, but `main.tsx` mounted it as a sibling
 * of `<App />` rather than a wrapper. Every `useToast()` caller therefore threw
 * in production while passing in tests — which is what blanked `/login`.
 */
export function AppProviders({ children, defaultTheme, storageKey }: AppProvidersProps) {
  return (
    <BrowserRouter>
      <ThemeProvider defaultTheme={defaultTheme} storageKey={storageKey}>
        <AuthProvider>
          <Toaster>{children}</Toaster>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
